import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { Layout } from "../components/layout/Layout";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import { MediaGrid } from "../components/media/MediaGrid";
import { User, Calendar } from "lucide-react";
import { tmdbApi } from "../services/tmdb";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";
import { SEOHead } from "../components/seo/SEOHead";
import { buildSEOTitle } from "../utils/seo";

const Container = styled.div`
  max-width: 1440px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  gap: var(--spacing-6);
  margin-bottom: var(--spacing-6);

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  width: 300px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    width: 100%;
    max-width: 300px;
    margin: 0 auto;
  }
`;

const ActorImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const PlaceholderImage = styled.div`
  width: 100%;
  aspect-ratio: 2/3;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
`;

const Info = styled.div`
  flex: 1;
`;

const Name = styled.h2`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-3);
  color: var(--color-text-primary);
`;

const MetaInfo = styled.div`
  display: flex;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-4);
  color: var(--color-text-secondary);
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
`;

const Biography = styled.p`
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin-bottom: var(--spacing-4);
`;

const Section = styled.section`
  margin-bottom: var(--spacing-6);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-4);
  color: var(--color-text-primary);
`;

const ActorFilmography = () => {
  const { id: personId } = useParams<{ id: string }>();
  
  // Scroll automatique vers le haut au chargement
  useScrollToTopOnMount();

  const { data: personDetails, isLoading } = useQuery({
    queryKey: ["person", personId],
    queryFn: () => personId ? tmdbApi.getPersonDetails(parseInt(personId)) : null,
    enabled: !!personId,
  });

  if (isLoading || !personDetails) return <LoadingSpinner />;

  const sortedMovies = personDetails.combined_credits?.cast
    ?.filter((credit) => credit.media_type === "movie" && credit.release_date)
    ?.sort((a, b) => {
      const dateA = new Date(a.release_date || "0");
      const dateB = new Date(b.release_date || "0");
      return dateB.getTime() - dateA.getTime();
    }) || [];

  return (
    <Layout>
      {(() => {
        const seoTitle = buildSEOTitle({
          baseTitle: personDetails.name,
          prefix: 'Filmographie de',
          hook: 'Tous ses films et séries',
        });
        const description = `${personDetails.name} – Biographie, date de naissance et filmographie complète (rôles récents en premier).`;
        const image = personDetails.profile_path ? `https://image.tmdb.org/t/p/w500${personDetails.profile_path}` : undefined;
        return (
          <SEOHead
            title={seoTitle}
            description={description}
            image={image}
            type="website"
          />
        );
      })()}
      <Container>
        <Header>
          <ImageContainer>
            {personDetails.profile_path ? (
              <ActorImage
                src={`https://image.tmdb.org/t/p/w500${personDetails.profile_path}`}
                alt={personDetails.name}
              />
            ) : (
              <PlaceholderImage>
                <User size={60} />
              </PlaceholderImage>
            )}
          </ImageContainer>

          <Info>
            <Name>{personDetails.name}</Name>

            <MetaInfo>
              {personDetails.birthday && (
                <MetaItem>
                  <Calendar size={20} />
                  {new Date(personDetails.birthday).toLocaleDateString()}
                </MetaItem>
              )}
              {personDetails.place_of_birth && (
                <MetaItem>{personDetails.place_of_birth}</MetaItem>
              )}
            </MetaInfo>

            {personDetails.biography && <Biography>{personDetails.biography}</Biography>}
          </Info>
        </Header>

        <Section>
          <SectionTitle>Filmographie</SectionTitle>
          {sortedMovies.length > 0 ? (
            <MediaGrid items={sortedMovies} type="movie" />
          ) : (
            <p>Aucun film trouvé</p>
          )}
        </Section>
      </Container>
    </Layout>
  );
};

export default ActorFilmography;
